import { useMemo, useState } from "react";
import { AutoSizer, InfiniteLoader, List, WindowScroller } from "react-virtualized";
import { NotifyContentListCard } from "../components/NotifyContentListCard";
import { useMedia } from "react-media";
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { useNotifySubscriptionGroupsPublic } from "../../../../hooks/useNotifySubscriptionGroupsPublic";
import { useInfinitePublicNotificationContent } from "../../../../hooks/useInfinitePublicNotificationContent";
import { NotifyPublicTopNavigation } from "../components/NotifyPublicTopNavigation";
import { Button, Container, Form, Header, Label, Modal } from "semantic-ui-react";
import "../../../../../public/PublicApp.scss";
import SubscriptionGroupsFilter from "../components/SubscriptionGroupsFilter";
import { AccessibleTextSearch } from "../../../../../../components/global_components/form_components/accessible/AccessibleTextSearch";
import { FocusScope } from "react-aria";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faXmark } from "@fortawesome/free-solid-svg-icons";

import PlaceholderUntilLoaded from "../../../../../../components/global_components/PlaceholderUntilLoaded";

const subscriptionGroupLabel = {
  MY_GROUPS: "My Groups", // groups a user is subscribed to/ a part of
  OTHER_GROUPS: "All Other Groups", // All public groupsa logged in user is NOT a member of
  PUBLIC_GROUPS: "Groups", // All public groups, mainly used for non-logged-in users
};

export function PublicPostsPage() {
  const isMobileView = useMedia({ query: "(max-width: 900px)" });
  const [isMobileFilterModalOpen, setIsMobileFilterModalOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useQueryState("query", parseAsString.withDefault(""));
  const [mobileFilterSubscriptionGroups, setMobileFilterSubscriptionGroups] = useState<number[]>([]); // Used for mobile Filtering of subscription Groups
  const debouncedFilterQuery = useDebounce(filterQuery, 350);

  const loadMoreRows = async () => {
    if (canFetchMoreNotificationContent && !isNotificationContentFetching) {
      return fetchMoreNotificationContent();
    }
    return Promise.resolve();
  };

  const { subscriberContactMethods, isLoggedIn, isLoggedInAnonymously } =
    useNotifySubscriberContactMethodsForLoggedInUser();

  const { publicSubscriptionGroups } = useNotifySubscriptionGroupsPublic();

  // Find all subscription groups that a logged in User is a member of
  const mySubscriptionGroups = Array.from(
    new Map(
      subscriberContactMethods
        ?.map((method) => method.subscriptionGroups)
        .flat()
        .map((group) => [group.id, group]),
    ).values(),
  );

  // ALL other Public subscriptions groups not in mySubscriptionGroups
  const otherSubscriptionGroups =
    publicSubscriptionGroups?.filter((group) => !mySubscriptionGroups.some((myGroup) => myGroup.id === group.id)) || [];

  // If you are logged in, the default state is having all subscription groups checked, if you are not logged in, the default is all public subscription groups
  const [filterSubscriptionGroups, setFilterSubscriptionGroups] = useQueryState(
    "groupIds",
    parseAsArrayOf(parseAsInteger).withDefault([]),
  );

  const {
    notifyContent,
    total,
    isNotificationContentLoading,
    isNotificationContentFetching,
    fetchMoreNotificationContent,
    canFetchMoreNotificationContent,
  } = useInfinitePublicNotificationContent({
    take: 10,
    query: debouncedFilterQuery,
    subscriptionGroupIds: filterSubscriptionGroups,
  });

  const filters = useMemo(
    () => ({
      query: debouncedFilterQuery,
    }),
    [debouncedFilterQuery, filterSubscriptionGroups],
  );

  const handleDesktopCheckboxChange = (groupId: number, isChecked: boolean) => {
    setFilterSubscriptionGroups((prev) => (isChecked ? [...prev, groupId] : prev.filter((id) => id !== groupId)));
  };

  const handleMobileCheckboxChange = (groupId: number, isChecked: boolean) => {
    setMobileFilterSubscriptionGroups((prev) => (isChecked ? [...prev, groupId] : prev.filter((id) => id !== groupId)));
  };

  // If it is checked, then check all tmySubscriptionGroup checkboxes
  const handleSelectAllMyGroupsChange = (key: string, isChecked: boolean) => {
    setFilterSubscriptionGroups((prev) => {
      const groupIds =
        key === "MyGroups"
          ? mySubscriptionGroups.map((group) => group.id)
          : otherSubscriptionGroups?.map((group) => group.id);
      return isChecked ? [...new Set([...prev, ...groupIds])] : prev.filter((id) => !groupIds.includes(id));
    });
  };

  // If it is checked, then check all tmySubscriptionGroup checkboxes
  const handleSelectAllSubscriptionGroupsChangeMobile = (key: string, isChecked: boolean) => {
    setMobileFilterSubscriptionGroups((prev) => {
      const groupIds =
        key === "MyGroups"
          ? mySubscriptionGroups.map((group) => group.id)
          : otherSubscriptionGroups?.map((group) => group.id);
      return isChecked ? [...new Set([...prev, ...groupIds])] : prev.filter((id) => !groupIds.includes(id));
    });
  };

  return (
    <PlaceholderUntilLoaded isLoading={isNotificationContentLoading}>
      <div>
        {/* Fixed Header */}
        <div className='tw-fixed tw-z-50 tw-w-full tw-bg-white'>
          <NotifyPublicTopNavigation />
        </div>

        {/* Main Container */}
        <Container className='tw-flex tw-flex-col  '>
          {/* Sticky Posts Section - Separate from filter area */}
          {isMobileView ? (
            // Mobile Header - scrolls away with posts section
            <div className='tw-bg-[#f5f7f8] tw-pt-20  '>
              <Header className='tw-mb-0' size='huge'>
                Posts
              </Header>
            </div>
          ) : (
            // Desktop Header - scrolls away
            <div className='tw-mb-[20px] tw-bg-[#f5f7f8] tw-pt-[114px]'>
              <Header className='tw-mb-0' size='huge'>
                Posts
              </Header>
              <div className='tw-mt-2 tw-h-[20px]'>
                {filterQuery !== "" && (
                  <p className='tw-mb-0 tw-mt-2 tw-text-[1.125rem] tw-text-black tw-opacity-[70%]' aria-live='polite'>
                    {total} {total === 1 ? "post" : "posts"} found for &quot;{filters.query}&quot;
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Mobile Search and Filter - Sticky */}
          {isMobileView && (
            <div className='tw-sticky tw-top-[60px] tw-z-10 tw-bg-[#f5f7f8] tw-pb-4 tw-pt-5  '>
              <div>
                <Label className='tw-bg-transparent tw-pl-0 tw-text-[16px] tw-font-bold tw-text-black'>
                  Search Posts
                </Label>
                <div className='tw-flex tw-flex-row'>
                  <div className='tw-basis-[60%]'>
                    <Form.Field className='tw-m-0'>
                      <AccessibleTextSearch
                        value={filterQuery}
                        onChange={(value: string) => setFilterQuery(value, { throttleMs: 500 })}
                      />
                    </Form.Field>
                  </div>
                  <div className='tw-mr-[0px] tw-basis-[40%] '>
                    <Form.Field className='tw-ml-3 tw-pr-[0px]'>
                      <Button
                        secondary
                        onClick={() => setIsMobileFilterModalOpen(true)}
                        className='tw-mr-0 tw-h-[2.5rem]'
                      >
                        <FontAwesomeIcon
                          icon={faFilter}
                          className='tw-mr-[.4375rem] tw-pr-[0px] tw-text-black tw-opacity-70'
                        />
                        Filter Groups
                      </Button>
                    </Form.Field>
                  </div>
                </div>
              </div>
              <div
                className='tw-mt-2 tw-text-[1.125rem] tw-text-black tw-opacity-[70%]'
                style={{ minHeight: "1.5rem" }}
                aria-live='polite'
              >
                {filterQuery !== "" && `${total} ${total === 1 ? "post" : "posts"} found for "${filters.query}"`}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className='tw-flex tw-min-h-0 tw-flex-1 '>
            {/* Posts List Section */}
            <div
              id='MyTestListContainer'
              className={`${isMobileView ? "tw-z-0 tw-w-full" : "tw-basis-[65%]"}`}
              style={isMobileView ? {} : { marginTop: "0px" }}
            >
              <InfiniteLoader
                isRowLoaded={({ index }) => !!notifyContent[index]}
                loadMoreRows={loadMoreRows}
                rowCount={total}
                threshold={10}
              >
                {({ onRowsRendered, registerChild }) => (
                  <WindowScroller>
                    {({ height, isScrolling, onChildScroll, scrollTop }) => (
                      <AutoSizer disableHeight>
                        {({ width }) => (
                          <List
                            autoHeight
                            height={height}
                            isScrolling={isScrolling}
                            onScroll={onChildScroll}
                            scrollTop={scrollTop}
                            onRowsRendered={onRowsRendered}
                            ref={registerChild}
                            rowCount={notifyContent.length}
                            rowHeight={200}
                            rowRenderer={({ index, key, style }) => {
                              const content = {
                                data: notifyContent[index],
                                isMobileView: isMobileView,
                              };
                              return (
                                <div key={key} style={{ ...style }}>
                                  <NotifyContentListCard {...content} />
                                </div>
                              );
                            }}
                            width={width}
                          />
                        )}
                      </AutoSizer>
                    )}
                  </WindowScroller>
                )}
              </InfiniteLoader>
            </div>

            {/* Desktop Filter Section - Sticky */}
            {!isMobileView && (
              <div className='tw-ml-[42px] tw-basis-[35%]'>
                <div className='tw-sticky tw-top-[75px] tw-z-[5] tw-bg-[#f5f7f8] tw-p-4'>
                  <Form.Field className='tw-m-0 tw-mb-4'>
                    <Label className='tw-bg-transparent tw-pl-0 tw-text-[16px] tw-font-bold tw-text-black'>
                      Search Posts
                    </Label>
                    <AccessibleTextSearch
                      value={filterQuery}
                      onChange={(value: string) => setFilterQuery(value, { throttleMs: 500 })}
                    />
                  </Form.Field>

                  {isLoggedIn || isLoggedInAnonymously ? (
                    <>
                      <SubscriptionGroupsFilter
                        groupLabel={subscriptionGroupLabel.MY_GROUPS}
                        groups={mySubscriptionGroups}
                        selectedGroups={filterSubscriptionGroups}
                        onSelectAll={(checked) => handleSelectAllMyGroupsChange("MyGroups", checked)}
                        onGroupChange={(groupId, checked) => handleDesktopCheckboxChange(groupId, checked)}
                      />
                      {otherSubscriptionGroups.length > 0 && (
                        <SubscriptionGroupsFilter
                          groupLabel={subscriptionGroupLabel.OTHER_GROUPS}
                          groups={otherSubscriptionGroups}
                          selectedGroups={filterSubscriptionGroups}
                          onSelectAll={(checked) => handleSelectAllMyGroupsChange("OtherGroups", checked)}
                          onGroupChange={(groupId, checked) => handleDesktopCheckboxChange(groupId, checked)}
                        />
                      )}
                    </>
                  ) : (
                    <SubscriptionGroupsFilter
                      groupLabel={subscriptionGroupLabel.PUBLIC_GROUPS}
                      groups={publicSubscriptionGroups}
                      selectedGroups={filterSubscriptionGroups}
                      onSelectAll={(checked) => handleSelectAllMyGroupsChange("OtherGroups", checked)}
                      onGroupChange={(groupId, checked) => handleDesktopCheckboxChange(groupId, checked)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Filter Modal */}
          <Modal
            open={isMobileFilterModalOpen}
            className='tw-fixed tw-bottom-0 tw-max-h-[80%] tw-w-screen tw-rounded-b-none'
            keyboardFocus
            onClose={() => setIsMobileFilterModalOpen(false)}
          >
            <FocusScope contain autoFocus restoreFocus>
              <Modal.Content className='tw-overflow-auto' scrolling={true}>
                <div className='tw-flex tw-w-full tw-items-center tw-justify-between' aria-label='Filter Modal Header'>
                  <h4
                    className='tw-mb-0 tw-text-[15px] tw-font-[700] focus:tw-ring-blue-dark focus:tw-ring-offset-2'
                    aria-label='Filters'
                  >
                    Filters
                  </h4>
                  <Button
                    className='button-ghost tw-flex tw-items-center'
                    aria-label='Close Modal'
                    tabIndex={0}
                    onClick={() => {
                      setIsMobileFilterModalOpen(false);
                      setMobileFilterSubscriptionGroups(filterSubscriptionGroups);
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faXmark}
                      className='tw-text-2xl tw-font-bold tw-text-black focus:tw-ring-blue-dark focus:tw-ring-offset-2'
                    />
                  </Button>
                </div>
                {isLoggedIn || isLoggedInAnonymously ? (
                  <>
                    <SubscriptionGroupsFilter
                      groupLabel={subscriptionGroupLabel.MY_GROUPS}
                      groups={mySubscriptionGroups}
                      selectedGroups={mobileFilterSubscriptionGroups}
                      onSelectAll={(checked) => handleSelectAllSubscriptionGroupsChangeMobile("MyGroups", checked)}
                      onGroupChange={(groupId, checked) => handleMobileCheckboxChange(groupId, checked)}
                    />
                    {otherSubscriptionGroups.length > 0 && (
                      <SubscriptionGroupsFilter
                        groupLabel={subscriptionGroupLabel.OTHER_GROUPS}
                        groups={otherSubscriptionGroups}
                        selectedGroups={mobileFilterSubscriptionGroups}
                        onSelectAll={(checked) => handleSelectAllSubscriptionGroupsChangeMobile("OtherGroups", checked)}
                        onGroupChange={(groupId, checked) => handleMobileCheckboxChange(groupId, checked)}
                      />
                    )}
                  </>
                ) : (
                  <SubscriptionGroupsFilter
                    groupLabel={subscriptionGroupLabel.PUBLIC_GROUPS}
                    groups={publicSubscriptionGroups}
                    selectedGroups={mobileFilterSubscriptionGroups}
                    onSelectAll={(checked) => handleSelectAllSubscriptionGroupsChangeMobile("OtherGroups", checked)}
                    onGroupChange={(groupId, checked) => handleMobileCheckboxChange(groupId, checked)}
                  />
                )}
              </Modal.Content>

              <Modal.Actions>
                <Button
                  primary
                  className='tw-mx-0 tw-w-[100%]'
                  onClick={() => {
                    setFilterSubscriptionGroups(mobileFilterSubscriptionGroups);
                    setIsMobileFilterModalOpen(false);
                  }}
                >
                  Apply Filters
                </Button>
              </Modal.Actions>
            </FocusScope>
          </Modal>
        </Container>
      </div>
    </PlaceholderUntilLoaded>
  );
}
